import * as React from "react";

import {
  Button,
  ButtonGroup,
  Grid,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";

import PersonIcon from "@mui/icons-material/Person";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  Book,
  Bookmark,
  BookmarkBorder,
  ContentCopy,
  ContentPaste,
  PlayCircleOutline,
  Download,
  ThumbUp,
  ThumbDown,
  ThumbUpOutlined,
  ThumbDownOutlined,
} from "@mui/icons-material";
import NotesIcon from "@mui/icons-material/Notes";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import InsightsIcon from "@mui/icons-material/Insights";

import { ReactComponent as ChatbotSVGBase } from "../../assets/chatbot.svg";
import hyperscaler_mapping from "../../data/modelMapping";

import { GET_TABLE, SAVE_USER_PROMPT, FEEDBACK_MODEL } from "../../endpoint";

import { useAppDispatch, useAppSelector } from "../../hooks/reduxhook";
import { addshowSources } from "../../redux/slices/showSourcesSlice";
import apiCall from "../../utils/apiCall";
import { addPrompts, getType } from "../../redux/slices/typeSlice";
import { useMsal } from "@azure/msal-react";
import TableeChatDisplay from "../TableChatDisplay";

import hyperScalerMapping from "../../data/hyperscalerMapping";
import { COLLECTION, NEO4J, PDF } from "../../data/constants";
import ExcerptLink from "./ExcerptLink";
import { NeoGraph } from "../Neograph";
import { getCypherDetails } from "../../redux/slices/conversationSlice";
import Markdown from "../Markdown";

import security_svg from "../../assets/security_secondary.svg";
import { addError } from "../../redux/slices/errorSlice";
import { getModelDetails } from "../../redux/slices/modelSlice";
import { useTheme } from "@mui/material";
import { generateAndDownloadDocx } from "../../utils/docxGenerator";
import {
  getChats,
  getconversationid,
} from "../../redux/slices/conversationSlice";
import { getFeedback, setFeedback } from "../../redux/slices/feedbackSlice";
import { IconButton } from "@mui/material";

const ModelImage = styled("img")({
  margin: "auto",
  width: "20px",
  height: "20px",
  objectFit: "contain",
});

const ChatbotSVG = styled(ChatbotSVGBase)(({ theme }) => ({
  margin: "auto",
  width: "20px",
  color: theme.palette.mode === "dark" ? "#aaa" : "#333",
}));

interface I_Sources {
  page?: string;
  source?: string;
  text?: string;
  time_stamp?: string;
  title?: string;
  publish_date?: string;
  author?: string;
  url?: string;
  excerpt?: string;
}

interface I_DbCreds {
  username: string;
  password: string;
  url: string;
}
interface IChatMessageProps {
  who: string;
  message:
    | {
        isError?: boolean;
        color?: string;
        detail?: string;
        result?: string;
        confidence_score?: number;
      }
    | string;
  sources: I_Sources[];
  model?: string;
  chat_type?: string;
  cypher: string;
  org_cypher: string;
  db_creds: I_DbCreds;
  image_model?: string;
  collection_chunks?: string[];
  collection_images?: string[];
  pii?: boolean;
  generated_image?: string;
  chatIndex?: number;
  messageId?: string;
  question?: string;
  conv_id?: string;
}

const ChatMessage: React.FunctionComponent<IChatMessageProps> = (props) => {
  const dispatch = useAppDispatch();

  const [copy, setCopy] = React.useState(false);
  const [bookmark, setBookmark] = React.useState(false);
  const [tableView, setTableView] = React.useState(false);
  const [tableData, setTableData] = React.useState([]);
  const [tableError, settableError] = React.useState("");
  const [showMore, setShowMore] = React.useState(false);
  const [cypher, setCypher] = React.useState(0);
  const [downloading, setDownloading] = React.useState(false);

  const chatType = useAppSelector(getType);
  const modelsmapping = useAppSelector(getModelDetails)?.modelmaps;
  const cypherDetails = useAppSelector(getCypherDetails);
  const modelDetails = useAppSelector(getModelDetails);
  const allChats = useAppSelector(getChats);
  const conversationId = useAppSelector(getconversationid);
  const feedback = useAppSelector((state) =>
    getFeedback(state, props.messageId || "")
  );

  const [liked, setLiked] = React.useState(false);
  const [disliked, setDisliked] = React.useState(false);

  React.useEffect(() => {
    setLiked(feedback.liked);
    setDisliked(feedback.disliked);
  }, [feedback.liked, feedback.disliked]);

  const { accounts } = useMsal();
  const account = accounts[0];

  const getMessage = () => {
    if (typeof props.message === "object" && props.message.isError) {
      if (props.message.detail === "Internal Server Error") {
        return "Error Occurred. Please try after sometime.!!!";
      }
      return `Error Occurred. Details: ${props.message.detail}`;
    }

    // Handle object message directly
    if (typeof props.message === "object" && props.message.result) {
      return props.message.result;
    }

    const messageStr =
      typeof props?.message === "string"
        ? props?.message
        : props?.message?.detail || "";

    // Check if the string starts with {'result': - indicating it's a stringified object
    if (messageStr.startsWith("{'result':")) {
      try {
        const messageObj = JSON.parse(messageStr.replace(/'/g, '"'));
        return messageObj.result || messageStr;
      } catch {
        // If JSON parsing fails, try to extract result using regex
        const resultMatch =
          messageStr.match(
            /'result':\s*'([\s\S]*?)'(?=\s*,\s*'\w+':|\s*}\s*$)/
          ) ||
          messageStr.match(
            /'result':\s*"([\s\S]*?)"(?=\s*,\s*'\w+':|\s*}\s*$)/
          );
        if (resultMatch) {
          return resultMatch[1]
            .replace(/\\n/g, "\n")
            .replace(/\\'/g, "'")
            .replace(/\\"/g, '"'); // Handle escaped quotes and newlines
        }
        return messageStr;
      }
    }

    // For regular strings, return as is
    return messageStr;
  };

  const getConfidenceScore = () => {
    // Handle object message directly
    if (typeof props.message === "object" && props.message.confidence_score) {
      return props.message.confidence_score;
    }

    const messageStr =
      typeof props?.message === "string"
        ? props?.message
        : props?.message?.detail || "";

    // Check if the string contains confidence_score
    if (messageStr.includes("confidence_score")) {
      try {
        const messageObj = JSON.parse(messageStr.replace(/'/g, '"'));
        return messageObj.confidence_score;
      } catch {
        // If JSON parsing fails, try to extract confidence_score using regex
        const scoreMatch = messageStr.match(/'confidence_score':\s*([0-9.]+)/);
        if (scoreMatch) {
          return parseFloat(scoreMatch[1]);
        }
        return null;
      }
    }

    return null;
  };

  const savePrompt = async () => {
    apiCall("POST", SAVE_USER_PROMPT, {
      prompt: getMessage(),
      user_email: account.username,
      chat_type: chatType.prompt_storage,
    })
      .then((res) => {
        dispatch(addPrompts(res.result));
        setBookmark(true);
      })
      .catch((err: any) => {
        dispatch(addError("Failed to save the user query.Please try again."));
      });
  };

  const seeTableView = () => {
    if (tableView) {
      setTableView(false);
    } else if (tableData.length === 0)
      apiCall("POST", GET_TABLE, {
        query: getMessage(),
        user_email: account.username,
      }).then((res: any) => {
        if (typeof res.result === "string") settableError(res.result);
        else {
          settableError("");
          setTableView(true);
          setTableData(res.result);
        }
      });
    else {
      setTableView(true);
    }
  };

  const textFormatter = (content: string) => {
    const splitArray = content.split(/\[Excerpt (\d+)\]/);
    return splitArray.map((part, index) => {
      if (isNaN(parseFloat(part))) {
        return <React.Fragment key={index}> {part}</React.Fragment>;
      } else {
        const data = props.sources.filter(
          (k) => k.excerpt?.toLowerCase() === "excerpt " + Number(part)
        );
        return (
          <ExcerptLink
            key={index}
            text={data[0].time_stamp ?? "hello"}
            index={index}
            {...data[0]}
          />
        );
      }
    });
  };

  const getModelKey = (modelName?: string) =>
    modelName
      ? modelDetails?.models?.chat
          ?.find((m) => m.model_name === modelName)
          ?.model.split("/")[0] || null
      : null;

  const parseMessageForDownload = (message: any) => {
    if (typeof message === "object" && message.result) {
      return message.result;
    }

    const messageStr =
      typeof message === "string" ? message : message?.detail || "";

    if (messageStr.includes("{'result':")) {
      try {
        const messageObj = JSON.parse(messageStr.replace(/'/g, '"'));
        return messageObj.result || messageStr;
      } catch {
        const resultMatch = messageStr.match(
          /['"]result['"]:\s*['"]([\s\S]*?)['"](?=\s*,\s*['"]\w+['"]:|\s*}\s*$)/
        );
        if (resultMatch) {
          return resultMatch[1]
            .replace(/\\n/g, "\n")
            .replace(/\\'/g, "'")
            .replace(/\\"/g, '"');
        }
        return messageStr;
      }
    }

    return messageStr;
  };

  const handleDownloadChat = async () => {
    if (props.chatIndex === undefined) return;

    setDownloading(true);
    try {
      let userQueryText = "";
      let aiResponseText = "";
      let modelName = props.model;

      if (props.who === "gpt") {
        // For AI responses, find the previous user query
        aiResponseText = parseMessageForDownload(props.message);

        // Look for the most recent user message before this AI response
        for (let i = props.chatIndex - 1; i >= 0; i--) {
          if (allChats[i]?.type === "user") {
            const userQuery = allChats[i].message;

            // Use the same logic as getMessage() to extract text
            if (
              typeof userQuery === "object" &&
              userQuery &&
              "result" in userQuery
            ) {
              userQueryText = (userQuery as any).result;
            } else {
              const messageStr =
                typeof userQuery === "string"
                  ? userQuery
                  : (userQuery as any)?.detail || "";
              userQueryText = parseMessageForDownload(userQuery);
            }
            break;
          }
        }

        if (!userQueryText) {
          userQueryText = "No user query found";
        }
      } else {
        // For user messages, find the next AI response
        userQueryText = getMessage();

        // Look for the next AI response after this user message
        for (let i = props.chatIndex + 1; i < allChats.length; i++) {
          if (allChats[i]?.type === "gpt") {
            console.log("Found AI chat:", allChats[i]);
            const aiResponse = allChats[i].message;
            modelName = allChats[i].model; // Get model from AI response
            console.log("AI chat model:", modelName);

            // Use the same logic as getMessage() to extract text
            aiResponseText = parseMessageForDownload(aiResponse);
            break;
          }
        }

        if (!aiResponseText) {
          aiResponseText = "No AI response found";
        }
      }

      // Try to get a default model if still undefined
      if (!modelName) {
        const defaultModel =
          modelDetails?.models?.chat?.[0]?.model_name || "Unknown Model";
        console.log("Using default model:", defaultModel);
        modelName = defaultModel;
      }

      console.log("aiResponseText", aiResponseText);

      await generateAndDownloadDocx(userQueryText, aiResponseText, modelName);
    } catch (error) {
      dispatch(addError("Failed to download chat. Please try again."));
    } finally {
      setDownloading(false);
    }
  };

  const processTextForCopy = (text: string): string => {
    const lines = text.split("\n");
    const processedLines: string[] = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];

      // Handle tables - convert to tab-separated format
      if (
        line.includes("|") &&
        i + 1 < lines.length &&
        lines[i + 1].includes("|")
      ) {
        const tableLines = [];
        let j = i;
        while (j < lines.length && lines[j].includes("|")) {
          tableLines.push(lines[j]);
          j++;
        }

        if (tableLines.length >= 2) {
          // Process table
          tableLines.forEach((tableLine, idx) => {
            if (idx === 1 && tableLine.includes("---")) {
              // Skip separator line
              return;
            }
            const cells = tableLine
              .split("|")
              .map((cell) => cell.trim())
              .filter((cell) => cell);
            processedLines.push(cells.join("\t"));
          });
          processedLines.push("");
          i = j;
          continue;
        }
      }

      // Process regular line using DOCX generator logic
      const parts = line.split(
        /(\*\*.*?\*\*|\*.*?\*|https?:\/\/\S+|\[\d+\]\(https?:\/\/\S+\))/g
      );
      const processedParts: string[] = [];
      let lastWasUrl = false;

      // Find all URL positions to determine the last URL
      const urlIndices = [];
      for (let i = 0; i < parts.length; i++) {
        if (
          /^\[\d+\]\(https?:\/\/\S+\)$/.test(parts[i]) ||
          (/^https?:\/\//.test(parts[i]) &&
            !parts[i].includes(".png") &&
            !parts[i].includes(".jpg") &&
            !parts[i].includes(".jpeg") &&
            !parts[i].includes(".gif") &&
            !parts[i].includes("blob.core.windows.net"))
        ) {
          urlIndices.push(i);
        }
      }

      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        const isLastUrl =
          urlIndices.length > 1 && i === urlIndices[urlIndices.length - 1];

        if (part.startsWith("**") && part.endsWith("**")) {
          processedParts.push(part.slice(2, -2).toUpperCase());
          lastWasUrl = false;
        } else if (
          part.startsWith("*") &&
          part.endsWith("*") &&
          !part.startsWith("**")
        ) {
          processedParts.push(`_${part.slice(1, -1)}_`);
          lastWasUrl = false;
        } else if (/^\[\d+\]\(https?:\/\/\S+\)$/.test(part)) {
          const match = part.match(/^\[(\d+)\]\((https?:\/\/\S+)\)$/);
          if (match) {
            if (lastWasUrl) {
              processedParts.push(isLastUrl ? " and " : ", ");
            }
            processedParts.push(match[2]);
            lastWasUrl = true;
          }
        } else if (
          /^https?:\/\//.test(part) &&
          !part.includes(".png") &&
          !part.includes(".jpg") &&
          !part.includes(".jpeg") &&
          !part.includes(".gif") &&
          !part.includes("blob.core.windows.net")
        ) {
          if (lastWasUrl) {
            processedParts.push(isLastUrl ? " and " : ", ");
          }
          processedParts.push(part);
          lastWasUrl = true;
        } else if (part && part.trim()) {
          processedParts.push(part);
          lastWasUrl = false;
        } else if (part === " " && !lastWasUrl) {
          processedParts.push(part);
        }
      }

      let processedLine = processedParts.join("");

      // Additional processing
      processedLine = processedLine
        .replace(/^#{1,6}\s+(.*)$/g, "$1")
        .replace(/^-\s+(.*)$/g, "• $1")
        .replace(/^\d+\.\s+(.*)$/g, "$1");

      processedLines.push(processedLine);
      i++;
    }

    return processedLines
      .join("\n")
      .replace(/\n\s*\n\s*\n/g, "\n\n")
      .trim();
  };

  const handleFeedback = async (type: "like" | "dislike") => {
    const newLiked = type === "like" ? !liked : false;
    const newDisliked = type === "dislike" ? !disliked : false;

    setLiked(newLiked);
    setDisliked(newDisliked);

    if (props.messageId) {
      dispatch(
        setFeedback({
          messageId: props.messageId,
          liked: newLiked,
          disliked: newDisliked,
        })
      );
    }

    // Find the question from chat history
    let question = "";
    if (props.chatIndex !== undefined) {
      for (let i = props.chatIndex - 1; i >= 0; i--) {
        if (allChats[i]?.type === "user") {
          const userQuery = allChats[i].message;
          if (
            typeof userQuery === "object" &&
            userQuery &&
            "result" in userQuery
          ) {
            question = (userQuery as any).result;
          } else {
            const messageStr =
              typeof userQuery === "string"
                ? userQuery
                : (userQuery as any)?.detail || "";
            try {
              const messageObj = JSON.parse(messageStr.replace(/'/g, '"'));
              question = messageObj.result || messageStr;
            } catch {
              question = messageStr;
            }
          }
          break;
        }
      }
    }

    // Get answer using getMessage which already extracts the result
    const answer = getMessage();

    try {
      await apiCall("POST", FEEDBACK_MODEL, {
        question: question,
        answer: answer,
        conv_id: conversationId,
        feedback: type === "like" ? "Like" : "Dislike",
      });
    } catch (error) {
      dispatch(addError("Failed to submit feedback. Please try again."));
    }
  };

  const theme = useTheme();

  return (
    <Grid
      container
      my={2}
      sx={{
        position: "relative",
      }}
    >
      <Grid
        container
        item
        sx={{
          background: theme.palette.mode === "dark" ? "#111" : "#fff",
          borderRadius: "50%",
          width: "35px",
          height: "35px",
          marginRight: "15px",
        }}
        justifyContent="center"
        alignItems="center"
      >
        {props.who === "gpt" ? (
          (() => {
            const modelKey = getModelKey(props.model);
            if (modelKey && hyperscaler_mapping[modelKey]) {
              return (
                <ModelImage
                  src={hyperscaler_mapping[modelKey]}
                  alt={props.model || "Default Bot"}
                />
              );
            } else {
              return <ChatbotSVG />;
            }
          })()
        ) : (
          <PersonIcon color="primary" />
        )}
      </Grid>
      <Grid
        item
        xs
        sx={{
          background: theme.palette.mode === "dark" ? "#111" : "#fff",
          borderRadius: "5px",
          padding: "10px 20px",
          marginRight: "10px",
        }}
      >
        {props.who === "gpt" && props.chat_type !== "video" ? (
          <>
            {tableView ? (
              tableError !== "" ? (
                <Typography>{tableError}</Typography>
              ) : (
                <TableeChatDisplay
                  rows={tableData?.map((t: object, i) => ({ ...t, id: i }))}
                  columns={[
                    ...Object.keys(tableData[0]).map((r) => ({
                      field: `${r}`,
                      headerName: `${r}`,
                      width: `${r.length * 20}`,
                      type: "number",
                    })),
                  ]}
                  pageSizeOptions={[5, 10, 25]}
                />
              )
            ) : cypher === 0 ? (
              <>
                {/* {props.message.split("\n").map((e, i) => (
                  <Typography
                    key={i}
                    sx={{
                      mb:
                        props.who === "gpt" &&
                        i + 1 === props.message.split("\n").length
                          ? 0
                          : 2,
                    }}
                  >
                    {e}
                  </Typography>
                ))} */}
                <Grid container justifyContent="space-between">
                  <div
                    style={{
                      flex: 1,
                      maxWidth: "calc(100% - 50px)",
                      overflowWrap: "break-word",
                      wordBreak: "break-word",
                    }}
                  >
                    {(typeof props.message === "string" &&
                      props.message.match(
                        /^https?:\/\/.*\.(jpg|jpeg|png|gif|webp|svg)$/i
                      )) ||
                    (typeof props.message === "string" &&
                      props.message.includes(
                        "dalleprodsec.blob.core.windows.net"
                      )) ? (
                      <img
                        src={props.message}
                        alt="Generated Image"
                        style={{
                          maxWidth: "100%",
                          height: "auto",
                          marginTop: "10px",
                          borderRadius: "8px",
                        }}
                      />
                    ) : (
                      getMessage()
                        .split("\n")
                        .map((m: string, i: number) => {
                          console.log("m", m);
                          return null;
                        }) && (
                        <>
                          <Markdown
                            markdownText={getMessage()}
                            isError={
                              typeof props.message === "object" &&
                              props.message.isError
                            }
                          />
                          {/* {getConfidenceScore() && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                mt: 1,
                                fontSize: "14px",
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="primary"
                                sx={{ mr: 1, fontSize: "14px" }}
                              >
                                Confidence:
                              </Typography>
                              <span>{getConfidenceScore()}%</span>
                            </Typography>
                          )} */}
                        </>
                      )
                    )}
                  </div>
                  <Tooltip title="Copy to clipboard">
                    {copy ? (
                      <ContentPaste color="primary" />
                    ) : (
                      <Tooltip title="copy">
                        <ContentCopy
                          color="primary"
                          sx={{
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            navigator.clipboard.writeText(
                              processTextForCopy(
                                parseMessageForDownload(props.message)
                              )
                            );
                            setCopy(true);
                            setTimeout(() => {
                              setCopy(false);
                            }, 2000);
                          }}
                          aria-label="copy icon"
                        />
                      </Tooltip>
                    )}
                  </Tooltip>
                </Grid>
                {/* <Grid container justifyContent="flex-end" mt={1}>
                  <Tooltip title="Like">
                    <IconButton
                      size="small"
                      onClick={() => handleFeedback("like")}
                    >
                      {liked ? (
                        <ThumbUp color="primary" />
                      ) : (
                        <ThumbUpOutlined color="primary" />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Dislike">
                    <IconButton
                      size="small"
                      onClick={() => handleFeedback("dislike")}
                    >
                      {disliked ? (
                        <ThumbDown color="primary" />
                      ) : (
                        <ThumbDownOutlined color="primary" />
                      )}
                    </IconButton>
                  </Tooltip>
                </Grid> */}
              </>
            ) : cypher === 1 ? (
              <Typography
                sx={{
                  mb: 2,
                }}
              >
                <i>{props.org_cypher}</i>
              </Typography>
            ) : (
              <div data-testid="NeoGraph">
                <NeoGraph
                  width={"100%"}
                  height={`200`}
                  containerId={"id1"}
                  neo4jUri={props?.db_creds?.url?.replace("+ssc", "") ?? ""}
                  neo4jUser={props?.db_creds?.username ?? ""}
                  neo4jPassword={props?.db_creds?.password ?? ""}
                  backgroundColor={"#fff"}
                  cypher={props.cypher}
                  alertWidth="97.5%"
                />
              </div>
            )}
          </>
        ) : props.who === "gpt" && props.chat_type === "video" ? (
          <>
            <Typography>{textFormatter(getMessage())}</Typography>
            <Grid container justifyContent="flex-end" mt={1}>
              <Tooltip title="Like">
                <IconButton size="small" onClick={() => handleFeedback("like")}>
                  {liked ? (
                    <ThumbUp color="primary" />
                  ) : (
                    <ThumbUpOutlined color="primary" />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title="Dislike">
                <IconButton
                  size="small"
                  onClick={() => handleFeedback("dislike")}
                >
                  {disliked ? (
                    <ThumbDown color="primary" />
                  ) : (
                    <ThumbDownOutlined color="primary" />
                  )}
                </IconButton>
              </Tooltip>
            </Grid>
          </>
        ) : (
          <Grid container>
            <Typography
              color="primary"
              sx={{
                flex: 1,
                width: "50%",
                flexWrap: "wrap",
                overflowWrap: "break-word",
              }}
            >
              {getMessage()
                .split("\n")
                .map((m: string, i: number) => {
                  // console.log("m", m);
                  return (
                    <span key={i}>
                      {m}
                      <br />
                    </span>
                  );
                })}
            </Typography>
            <Grid>
              <Tooltip title="Copy to clipboard">
                {copy ? (
                  <ContentPaste color="primary" />
                ) : (
                  <Tooltip title="copy">
                    <ContentCopy
                      color="primary"
                      sx={{
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        navigator.clipboard.writeText(getMessage());
                        setCopy(true);
                        setTimeout(() => {
                          setCopy(false);
                        }, 2000);
                      }}
                      aria-label="copy icon"
                    />
                  </Tooltip>
                )}
              </Tooltip>
              <Tooltip title="Save as query">
                {bookmark ? (
                  <Bookmark color="secondary" aria-label="book-mark" />
                ) : (
                  <BookmarkBorder
                    color="primary"
                    sx={{
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      savePrompt();
                      setBookmark(true);
                      setTimeout(() => {
                        setBookmark(false);
                      }, 2000);
                    }}
                    aria-label="book-mark-border"
                  />
                )}
              </Tooltip>

              <Tooltip title="Download as DOCX">
                <Download
                  color="primary"
                  sx={{
                    cursor: downloading ? "not-allowed" : "pointer",
                    opacity: downloading ? 0.5 : 1,
                  }}
                  onClick={downloading ? undefined : handleDownloadChat}
                  aria-label="download-docx"
                />
              </Tooltip>
            </Grid>
          </Grid>
        )}
        {props.sources.length > 0 &&
          (props.chat_type === PDF || props.chat_type === "rag") && (
            <Grid container direction="column" my={1.5}>
              <Grid container alignItems="center" mb={1}>
                <Book color="primary" />
                <Typography color="primary">Sources:</Typography>
              </Grid>
              {props.sources.map(
                (source, i) =>
                  (i < 5 || showMore) && (
                    <Grid container alignItems="center" key={i} mb={0.5}>
                      <Typography color="primary" sx={{ mr: 1, ml: 0.5 }}>
                        Source {i + 1}:
                      </Typography>
                      <Typography sx={{ mr: 0.5 }}>Page</Typography>
                      <Typography
                        onClick={() =>
                          dispatch(
                            addshowSources({
                              index: i,
                              sources: props.sources,
                            })
                          )
                        }
                        sx={{
                          cursor: "pointer",
                        }}
                      >
                        {Number(source.page) >= 0 && <>{source.page} of </>}
                        {source?.source?.split("/")[2].split("---")[0]}.
                        {source?.source?.split("/")[2].split(".")[2]}
                      </Typography>
                    </Grid>
                  )
              )}
            </Grid>
          )}
        {props.sources.length > 4 &&
          (props.chat_type === PDF || props.chat_type === "rag") && (
            <Button
              variant="text"
              sx={{
                marginLeft: "auto",
                marginTop: "-10px",
              }}
              size="small"
              color="primary"
              onClick={() => setShowMore((prevShowMore) => !prevShowMore)}
            >
              {showMore ? (
                <>
                  Show less sources
                  <ExpandLessIcon />
                </>
              ) : (
                <>
                  Show more sources
                  <ExpandMoreIcon />
                </>
              )}
            </Button>
          )}
        {props.image_model && (
          <>
            <Grid
              container
              direction="column"
              my={2}
              sx={{
                borderTop: "1px solid rgba(0,0,0,0.1)",
                pt: 1,
              }}
            >
              {props.collection_chunks !== undefined &&
                props.collection_chunks?.length > 0 && (
                  <Grid container>
                    <img
                      src={hyperScalerMapping[`rag`]?.icon}
                      alt={hyperScalerMapping[`rag`]?.displayName}
                      width="20px"
                    />
                    <Typography>
                      Sources:&nbsp;
                      {props.collection_chunks?.map((chunk, i) => {
                        const names = chunk.split("---");
                        return (
                          <span
                            key={i}
                            style={{
                              marginRight: "5px",
                            }}
                          >
                            {names[0]}.{names[names.length - 1]},
                          </span>
                        );
                      })}
                    </Typography>
                  </Grid>
                )}{" "}
              {props.collection_images !== undefined &&
                props.collection_images?.length > 0 && (
                  <Grid container mt={1}>
                    <img
                      src={hyperScalerMapping[`image`]?.icon}
                      alt={hyperScalerMapping[`image`]?.displayName}
                      width="20px"
                    />
                    <Typography>
                      Sources:&nbsp;
                      {props.collection_images?.map((chunk, i) => {
                        const names = chunk.split("---");
                        return (
                          <span
                            key={i}
                            style={{
                              marginRight: "5px",
                            }}
                          >
                            {names[0]}.{names[names.length - 1]},
                          </span>
                        );
                      })}
                    </Typography>
                  </Grid>
                )}
            </Grid>
          </>
        )}

        {/* {props.who === "gpt" && (
          
        )} */}

        {props.who === "gpt" && (
          <Grid
            container
            justifyContent="space-between"
            direction="row"
            alignItems="center"
            mt={2}
          >
            <Grid
              display="flex"
              container
              alignItems="center"
              justifyContent="center"
              flex={1}
              sx={{
                // border: "1px solid #FD5F07",
                p: 0.5,
                borderRadius: "10px",
                width: "350px",
                cursor: "pointer",
              }}
              // onClick={seeTableView}
            >
              {props.chat_type === "neo4j" && (
                <ButtonGroup>
                  <Button
                    variant="outlined"
                    startIcon={<NotesIcon fontSize="small" />}
                    size="small"
                    onClick={() => setCypher(0)}
                  >
                    Text
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<AcUnitIcon fontSize="small" />}
                    size="small"
                    onClick={() => setCypher(1)}
                  >
                    Cypher
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<InsightsIcon fontSize="small" />}
                    size="small"
                    onClick={() => setCypher(2)}
                  >
                    Graph
                  </Button>
                </ButtonGroup>
              )}
            </Grid>
            {props.model !== "" && (
              <Grid
                display="flex"
                direction="row"
                justifyContent="space-evenly"
              >
                {props.pii && (
                  <Grid
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    fontStyle="italic"
                    sx={{
                      p: 0.5,
                      mr: 2,
                    }}
                  >
                    <img src={security_svg} alt="PII" width={20} />
                    <Typography fontSize="small">PII Redacted</Typography>
                  </Grid>
                )}
                {/* <Grid
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  fontStyle="italic"
                  sx={{
                    p: 0.5,
                    mr: 1,
                  }}
                >
                  {props.image_model && (
                    <>
                      <img
                        src={hyperScalerMapping[`rag`]?.icon}
                        alt={hyperScalerMapping[`rag`]?.displayName}
                        width="20px"
                      />
                      <span
                        style={{
                          marginRight: "5px",
                        }}
                      >
                        :
                      </span>
                    </>
                  )}
                  {props?.model && (
                    <>
                      <img
                        src={modelsmapping[`${props?.model}`]?.icon}
                        alt={modelsmapping[`${props?.model}`]?.name}
                        width="20px"
                      />
                      &nbsp;
                      <Typography
                        color="primary"
                        sx={{
                          textTransform: "capitalize",
                        }}
                      >
                        {modelsmapping[`${props?.model}`]?.name
                          .replaceAll(
                            process.env?.REACT_APP_DEFAULT_MODEL_REMOVE ?? "",
                            ""
                          )
                          .replaceAll("-", " ")
                          .replaceAll("AI_Team", "")
                          .replaceAll("iAura_team", "iAura")}
                      </Typography>
                    </>
                  )}
                </Grid> */}
                {/* {props.image_model && (
                  <Grid
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    fontStyle="italic"
                    sx={{
                      p: 0.5,
                    }}
                  >
                    <img
                      src={hyperScalerMapping[`image`]?.icon}
                      alt={hyperScalerMapping[`image`]?.displayName}
                      width="20px"
                    />
                    <span
                      style={{
                        marginRight: "5px",
                      }}
                    >
                      :
                    </span>
                    <img
                      src={modelsmapping[`${props?.image_model}`]?.icon}
                      alt={modelsmapping[`${props?.image_model}`]?.name}
                      width="20px"
                    />
                    <Typography color="primary">
                      {modelsmapping[`${props?.image_model}`]?.name
                        ?.replaceAll(
                          process.env?.REACT_APP_DEFAULT_MODEL_REMOVE ?? "",
                          ""
                        )
                        .replaceAll("-", " ")
                        .replaceAll("AI_Team", "")
                        .replaceAll("iAura_team", "iAura")}
                    </Typography>
                  </Grid>
                )} */}
                {/* <Grid
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  sx={{
                    p: 0.5,
                    ml: 3,
                  }}
                >
                  {hyperScalerMapping[`${props.chat_type}`]?.icon && (
                    <img
                      src={hyperScalerMapping[`${props.chat_type}`]?.icon}
                      alt="Chat Type"
                      width="16px"
                      style={{
                        color: theme.palette.mode === "dark" ? "#fff" : "#333",
                      }}
                    />
                  )}
                  &nbsp;
                  <Typography
                    color="primary"
                    sx={{
                      textTransform: "capitalize",
                    }}
                  >
                    {hyperScalerMapping[`${props.chat_type}`]?.displayName}
                  </Typography>
                </Grid> */}
              </Grid>
            )}
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default ChatMessage;
